/**
 * Eigenbau: Mono-Reiter mit Tabmarke — Tremors Tabs tragen Flächen und Rahmen.
 *
 * Der Entwurf führt Reiter als reine Mono-Schriftzeile: Der aktive Reiter
 * trägt eine 2px-Unterstreichung in `--tabmarke` (das helle Mint), die
 * inaktiven stehen in `--tinte4` und färben sich beim Überfahren. Keine
 * Fläche, kein Rahmen, keine Rundung — die Marke ist die ganze Auskunft.
 *
 * Zwei Bauformen je nach Ort: Über einer Tintenlinie (Einstellungen) sitzt
 * der Reiter mit `pb-3` und `-mb-px`, damit die Marke die Linie berührt;
 * frei stehend (Board, Lieferung) mit `pb-1.5`.
 */

import type { ReactNode } from "react";
import { cx, focusRing } from "@/lib/utils";

export function Reiterleiste({
  children,
  linie = false,
  className,
}: {
  children: ReactNode;
  /** Reiter sitzt auf einer Tintenlinie — Marke berührt sie (Einstellungen). */
  linie?: boolean;
  className?: string;
}) {
  return (
    <div
      className={cx(
        "flex items-center gap-[22px] font-mono text-[12.5px]",
        linie && "border-b border-tinte",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function Reiter({
  aktiv,
  onKlick,
  children,
  className,
  testId,
}: {
  aktiv: boolean;
  onKlick?: () => void;
  children: ReactNode;
  className?: string;
  testId?: string;
}) {
  return (
    <button
      type="button"
      onClick={onKlick}
      aria-current={aktiv ? "true" : undefined}
      data-testid={testId}
      className={cx(
        "cursor-pointer pb-3 transition-colors duration-150 ease-aus",
        /*
          Der Versatz gehört an den **aktiven** Reiter, nicht an die ruhenden:
          Nur er trägt die Marke, und nur sie soll die Tintenlinie überdecken
          statt sie zu doppeln. Der Entwurf schreibt `margin-bottom: -1px`
          genau dort. Vorher hing er an den ruhenden, wo er nichts bewirkt und
          sie stattdessen um eine Linienstärke tiefer setzte.
        */
        aktiv
          ? "-mb-px font-medium shadow-[inset_0_-2px_0_0_var(--tabmarke)]"
          : "font-normal text-tinte4 hover:text-tinte",
        focusRing,
        className,
      )}
    >
      {children}
    </button>
  );
}
